import { useEffect, useRef, useState } from 'react';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import styles from './AlzaferHospitalCaseStudy.module.css';
import heroImg from '../../images/alzafar-hero.png';
import alzafarImg2 from '../../images/alzafar-2.png';
import alzafarImg3 from '../../images/alzafar-3.png';
import alzafarImg4 from '../../images/alzafar-4.png';
import alzafarImg5 from '../../images/alzafar-5.png';

const slides = [
  {
    src: heroImg,
    title: 'Cover Design',
    caption: 'A clean clinical opening with clear title hierarchy.',
  },
  {
    src: alzafarImg2,
    title: 'VTE Introduction',
    caption: 'Definition and nursing context organised for quick reading.',
  },
  {
    src: alzafarImg3,
    title: 'Role of Nurses',
    caption: 'Core responsibilities presented with a restrained layout.',
  },
  {
    src: alzafarImg4,
    title: 'Effective Charting',
    caption: 'Documentation guidance structured as practical actions.',
  },
  {
    src: alzafarImg5,
    title: 'Conclusion',
    caption: 'A focused closing slide that reinforces patient safety.',
  },
];

const projectMeta = [
  ['Client', 'Alzafer Hospital'],
  ['Project', 'Healthcare Presentation'],
  ['Deliverable', '11-slide deck'],
  ['Focus', 'Nursing Education'],
];

function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.dataset.visible = 'true';
          observer.unobserve(node);
        }
      },
      {
        threshold: 0.14,
        rootMargin: '0px 0px -48px',
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();

  return (
    <div
      ref={ref}
      className={`${styles.reveal} ${className}`}
      style={{ '--reveal-delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function AlzaferHospitalCaseStudy() {
  const [activeSlide, setActiveSlide] = useState(null);

  useEffect(() => {
    if (activeSlide === null) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setActiveSlide(null);

      if (event.key === 'ArrowRight') {
        setActiveSlide((current) => (current + 1) % slides.length);
      }

      if (event.key === 'ArrowLeft') {
        setActiveSlide(
          (current) => (current - 1 + slides.length) % slides.length,
        );
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSlide]);

  const showPrevious = () => {
    setActiveSlide(
      (current) => (current - 1 + slides.length) % slides.length,
    );
  };

  const showNext = () => {
    setActiveSlide((current) => (current + 1) % slides.length);
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('graphic');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <main className={styles.page}>
      <section className={styles.hero}>
        <img src={heroImg} alt="Alzafer Hospital presentation" className={styles.heroBgImage} />
        <div className={styles.heroOverlay} />
        <div className={styles.heroShape} aria-hidden="true" />
        <div className={styles.heroLine} aria-hidden="true" />

        <div className={styles.container}>
          <div className={styles.heroGrid}>
            <Reveal className={styles.heroCopy}>
              <p className={styles.eyebrow}>
                Presentation Design · Healthcare
              </p>

              <h1>
                Alzafer
                <span>Hospital</span>
              </h1>

              <p className={styles.lead}>
                A concise nursing education presentation about VTE prevention
                and the importance of accurate clinical charting.
              </p>

              <div className={styles.tags} aria-label="Project skills">
                <span>Healthcare Design</span>
                <span>Information Hierarchy</span>
                <span>Presentation Layout</span>
              </div>
            </Reveal>

            <Reveal className={styles.heroVisual} delay={120}>
              <button
                type="button"
                className={styles.heroPreview}
                onClick={() => setActiveSlide(0)}
                aria-label="Open presentation cover preview"
              >
                <img
                  src={heroImg}
                  alt="Alzafer Hospital VTE prevention presentation cover"
                  fetchPriority="high"
                />
                <span className={styles.previewBadge}>View screen ↗</span>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className={styles.overview}>
        <div className={styles.container}>
          <Reveal className={styles.overviewGrid}>
            <div>
              <p className={styles.sectionLabel}>Project overview</p>
              <h2>Clinical information made easier to understand.</h2>
            </div>

            <p className={styles.overviewText}>
              The deck uses a consistent hospital palette, generous spacing and
              concise content blocks to help nurses scan important VTE
              prevention and documentation guidance quickly.
            </p>
          </Reveal>

          <Reveal className={styles.metaGrid} delay={90}>
            {projectMeta.map(([label, value]) => (
              <div className={styles.metaItem} key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className={styles.gallerySection}>
        <div className={styles.container}>
          <Reveal className={styles.sectionHeading}>
            <div>
              <p className={styles.sectionLabel}>Selected screens</p>
              <h2>A small selection from the final presentation.</h2>
            </div>

            <p>Click a screen to open the larger preview.</p>
          </Reveal>

          <div className={styles.gallery}>
            {slides.slice(1).map((slide, index) => (
              <Reveal
                key={slide.title}
                delay={index * 70}
                className={`${styles.slideCard} ${
                  index === 0 || index === 3 ? styles.wideCard : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveSlide(index + 1)}
                  aria-label={`Open ${slide.title}`}
                >
                  <span className={styles.imageFrame}>
                    <img src={slide.src} alt={slide.title} loading="lazy" />
                    <span className={styles.cardArrow} aria-hidden="true">
                      ↗
                    </span>
                  </span>

                  <span className={styles.cardCopy}>
                    <span className={styles.cardNumber}>
                      {String(index + 2).padStart(2, '0')}
                    </span>

                    <span>
                      <strong>{slide.title}</strong>
                      <small>{slide.caption}</small>
                    </span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.nextSection}>
        <div className={styles.container}>
          <Reveal className={styles.nextCard}>
            <div>
              <p className={styles.sectionLabel}>More work</p>
              <h2>Explore more selected design projects.</h2>
            </div>

            <a href="/#projects">
              View all projects
              <span aria-hidden="true">↗</span>
            </a>
          </Reveal>
        </div>
      </section>

      <Footer mode={mode} background="black" />

      {activeSlide !== null && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${slides[activeSlide].title} preview`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveSlide(null);
          }}
        >
          <button
            type="button"
            className={styles.closeButton}
            onClick={() => setActiveSlide(null)}
            aria-label="Close preview"
          >
            ×
          </button>

          <button
            type="button"
            className={`${styles.navButton} ${styles.previousButton}`}
            onClick={showPrevious}
            aria-label="Previous screen"
          >
            ←
          </button>

          <figure className={styles.lightboxFigure}>
            <img
              src={slides[activeSlide].src}
              alt={slides[activeSlide].title}
            />
            <figcaption>
              <span>
                {String(activeSlide + 1).padStart(2, '0')} /{' '}
                {String(slides.length).padStart(2, '0')}
              </span>
              <strong>{slides[activeSlide].title}</strong>
            </figcaption>
          </figure>

          <button
            type="button"
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={showNext}
            aria-label="Next screen"
          >
            →
          </button>
        </div>
      )}
    </main>
    </>
  );
}